import React, { useState, useEffect } from "react"
import DynamicActionBar from "../../../components/ui/dynamic-action"
import { computeAttentionData } from "../../../utils/attentionHelpers"
import { Users, BookOpen, CalendarClock, Award, Wallet, Server, Mail } from "lucide-react"

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 0 }).format(amount || 0)

const AttentionContent = ({ title, headline, detail, actionLabel, onAction, allClear }) => (
  <div className="flex flex-col justify-between h-full p-5">
    <div>
      <p className="text-sm text-muted-foreground mb-1">{title}</p>
      <p className="text-lg font-bold text-foreground leading-snug">{headline}</p>
      {detail && <p className="text-sm text-muted-foreground mt-1">{detail}</p>}
    </div>
    {!allClear && (
      <button onClick={onAction} className="self-start text-sm font-medium text-primary hover:underline mt-3">
        {actionLabel} →
      </button>
    )}
  </div>
)

const NeedsAttentionPanel = ({ orgId, onNavigate }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    computeAttentionData(orgId).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId])

  if (loading || !data) {
        return <div className="h-14 w-full max-w-[410px] rounded-2xl bg-muted animate-pulse" />
  }

  const actions = [
    {
      id: "learners",
      label: "Learners",
      icon: Users,
      dimensions: { width: 380, height: 140 },
      content: (
        <AttentionContent
          title="Learners"
          headline={
            data.learners.inactiveCount > 0
              ? `${data.learners.inactiveCount} learners inactive 14+ days`
              : "All learners are active"
          }
          allClear={data.learners.inactiveCount === 0}
          actionLabel="View learners"
          onAction={() => onNavigate?.("learners")}
        />
      ),
    },
    {
      id: "courses",
      label: "Courses",
      icon: BookOpen,
      dimensions: { width: 400, height: 150 },
      content: (
        <AttentionContent
          title="Courses"
          headline={
            data.courses.belowAverageCount > 0
              ? `${data.courses.belowAverageCount} courses below org average`
              : "All courses on track"
          }
          detail={data.courses.titles?.slice(0, 2)?.join(", ")}
          allClear={data.courses.belowAverageCount === 0}
          actionLabel="Review courses"
          onAction={() => onNavigate?.("courses")}
        />
      ),
    },
    {
      id: "programs",
      label: "Programs",
      icon: CalendarClock,
      dimensions: { width: 400, height: 150 },
      content: (
        <AttentionContent
          title="Programs"
          headline={
            data.programs.upcomingCount > 0
              ? `${data.programs.upcomingCount} programs with deadlines in 30 days`
              : "No upcoming program deadlines"
          }
          detail={data.programs.titles?.slice(0, 2)?.join(", ")}
          allClear={data.programs.upcomingCount === 0}
          actionLabel="View programs"
          onAction={() => onNavigate?.("learning-programs")}
        />
      ),
    },
    {
      id: "credentials",
      label: "Credentials",
      icon: Award,
      dimensions: { width: 380, height: 140 },
      content: (
        <AttentionContent
          title="Credentials"
          headline={
            data.credentials.pendingCount > 0
              ? `${data.credentials.pendingCount} credentials pending issuance`
              : "No credentials pending"
          }
          allClear={data.credentials.pendingCount === 0}
          actionLabel="Review credentials"
          onAction={() => onNavigate?.("certificates")}
        />
      ),
    },
    {
      id: "payments",
      label: "Payments",
      icon: Wallet,
      dimensions: { width: 380, height: 140 },
      content: (
        <AttentionContent
          title="Payments"
          headline={
            data.payments.pendingCount > 0
              ? `${formatCurrency(data.payments.pendingAmount, data.payments.currency)} in pending payments`
              : "No pending payments"
          }
          detail={data.payments.pendingCount > 0 ? `${data.payments.pendingCount} orders` : null}
          allClear={data.payments.pendingCount === 0}
          actionLabel="View transactions"
          onAction={() => onNavigate?.("commerce-orders")}
        />
      ),
    },
        {
      id: "invitations",
      label: "Invitations",
      icon: Mail,
      dimensions: { width: 380, height: 140 },
      content: (
        <AttentionContent
          title="Invitations"
          headline={
            data.invitations.expiringCount > 0
              ? `${data.invitations.expiringCount} invitations expiring within 7 days`
              : "No invitations expiring soon"
          }
          allClear={data.invitations.expiringCount === 0}
          actionLabel="View invitations"
          onAction={() => onNavigate?.("people-invitations")}
        />
      ),
    },
    {
      id: "api",
      label: "API",
      icon: Server,
      dimensions: { width: 380, height: 140 },
      content: (
        <AttentionContent
          title="API"
          headline={
            data.api.errorRate > 0
              ? `API error rate at ${data.api.errorRate}% this week`
              : data.api.totalRequests > 0
              ? "API running clean this week"
              : "No API activity logged yet"
          }
          allClear={data.api.errorRate === 0}
          actionLabel="View API usage"
          onAction={() => onNavigate?.("developer-api-usage")}
        />
      ),
    },
  ]

  return (
    <div>
      <p className="text-sm font-semibold text-muted-foreground mb-2">Needs Your Attention</p>
      <DynamicActionBar actions={actions} />
    </div>
  )
}

export default NeedsAttentionPanel